import { Component, h } from 'preact'
import axios from 'axios'
import Header from '../header'
import { Bar } from 'react-chartjs-2'
import {ChartData, ChartOptions, ChartTooltipItem, TimeUnit} from 'chart.js'
import { DashboardTimezoneDisclaimer } from './DashboardTimezoneDisclaimer'
import { FilterOptionDate, StoredState } from './interfaces'
import AnalyticsDateFormatter from './AnalyticsDateFormatter'
import DashboardDateRangeFilter from './DashboardDateRangeFilter'
import {
  getBrandBlue,
  getBrandBorderGrey,
  getBrandYellow,
  getDefaultDateRange,
  getDefaultSelectedDate,
  displayLegendsWithMarginBottom,
  trackDashboardUsage
} from './utils'
import { Loader } from './Loader'
import QueryParameters from './QueryParameters'
import { GraphTitle } from './GraphTitle'
import {subscribe} from "../../utils/globals";
displayLegendsWithMarginBottom()

interface Data {
  count: number,
  time: number
}

interface Hash<T> {
  [key: string]: T;
}

interface Props {}

interface State {
  pageViewData: Array<Data>,
  uniqueVisitorsData: Array<Data>,
  activeCustomersData: Array<Data>,
  customers: Array<Data>,
  selectedPresetDateRangeId: string,
  startDate: Date,
  endDate: Date,
  selectedDate: FilterOptionDate,
  presetDateRangeList: Array<FilterOptionDate>,
  isDateRangeOpen: boolean,
  storedState: StoredState,
  isLoading: boolean,
  isPageViewsLoading: boolean,
  isUniqueVisitorsLoading: boolean,
  isTotalVsActiveCustomersLoading: boolean,
  isPercentageMonthlyActiveCustomersLoading: boolean,
  isSidebarMenuOpen: boolean,
  shouldChartRedraw: boolean
}

class EngagementGraphs extends Component<Props, State> {
  private brandBlue = getBrandBlue()
  private brandYellow = getBrandYellow()
  private borderGrey = getBrandBorderGrey()
  wrapperRef: null | HTMLElement = null
  barChart: null | HTMLElement = null
  resizeTimeout: number | undefined
  constructor () {
    super()
    let monthStartDate = AnalyticsDateFormatter.getMonthStartDate()
    let yesterday = AnalyticsDateFormatter.getYesterday()
    const queryParams = new QueryParameters()
    this.setState({
      pageViewData: [],
      uniqueVisitorsData: [],
      activeCustomersData: [],
      customers: [],
      selectedPresetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
      storedState: {
        presetDateRangeId: queryParams.has('date_range') ? queryParams.get('date_range') : 'last_thirty_days',
        selectedDateRange: getDefaultSelectedDate()
      },
      startDate: monthStartDate,
      endDate: yesterday,
      presetDateRangeList: [],
      isDateRangeOpen: false,
      isLoading: false,
      isSidebarMenuOpen: true,
      shouldChartRedraw: false,
    })

    this.toggleDateRange = this.toggleDateRange.bind(this)
    this.onDateRangeChanged = this.onDateRangeChanged.bind(this)
    this.onResetDateRange = this.onResetDateRange.bind(this)
    this.onPresetDateRangeChanged = this.onPresetDateRangeChanged.bind(this)
    this.onCustomDateRangeChanged = this.onCustomDateRangeChanged.bind(this)
  }

  async componentDidMount () {
    trackDashboardUsage('engagement')
    subscribe('control_sidebar_state', (payload) => this.setState({ isSidebarMenuOpen: payload.isOpen }))
    this.redrawGraphsOnResize()

    this.setState({
      isLoading: true,
      isPageViewsLoading: true,
      isUniqueVisitorsLoading: true,
      isTotalVsActiveCustomersLoading: true,
      isPercentageMonthlyActiveCustomersLoading: true
    })
    try {
      const presetDateRanges = await this.fetchDateRangeFilter()
      let defaultDateRange = getDefaultDateRange(presetDateRanges, this.state.selectedPresetDateRangeId)
      let updatedState = Object.assign({}, this.state.storedState)
      updatedState.presetDateRangeId = defaultDateRange.id
      updatedState.selectedDateRange = defaultDateRange

      if ('custom_range' === this.state.selectedPresetDateRangeId) {
        const queryParameters = new QueryParameters()
        defaultDateRange = {
          id: 'custom_range',
          label: 'Custom range',
          startDate: new Date(queryParameters.get('date_from')),
          endDate: new Date(queryParameters.get('date_till'))
        }
        updatedState.selectedDateRange = getDefaultSelectedDate()
      }

      this.setState({
        presetDateRangeList: presetDateRanges,
        selectedDate: defaultDateRange,
        startDate: new Date(defaultDateRange.startDate),
        endDate: new Date(defaultDateRange.endDate),
        storedState: updatedState,
        isLoading: false
      })
    } catch {
      this.setState({ isLoading: false })
    }
    this.fetchPageViewData()
    this.fetchUniqueVisitorsData()
    this.fetchActiveCustomersData()
    this.fetchTotalCustomers()
  }

  addHandler () {
    const { isDateRangeOpen } = this.state
    if(isDateRangeOpen ) {
      window.document.addEventListener('mousedown', this.handleClickOutside)
    }
  }

  removeHandler () {
    window.document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e: MouseEvent) => {
    if (!this.wrapperRef) return
    if (!this.wrapperRef.contains(e.target as HTMLElement)) {
      this.onResetDateRange()
      this.setState({}, this.removeHandler)
    }
  }

  redrawGraphsOnResize ()  {
    if (typeof window.ResizeObserver === "undefined") {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimeout)
      this.setState({ shouldChartRedraw: true })
      this.resizeTimeout = window.setTimeout(() => this.setState({shouldChartRedraw: false}), 2*10)
    })

    if(this.barChart != null) {
      resizeObserver.observe(this.barChart);
    }
  }


  async fetchDateRangeFilter () {
    const res = await axios.get('/analyticsV2/fetchDateRangeFilter')
    return res.data.ranges
  }

  async fetchUniqueVisitorsData () {
    this.setState({ isUniqueVisitorsLoading: true })
    try {
      const response = await this.fetchViewData('unique_visitors')
      this.setState({
        uniqueVisitorsData: response,
        isUniqueVisitorsLoading: false
      })
    } catch {
      this.setState({ isUniqueVisitorsLoading: false })
    }
  }

  async fetchPageViewData () {
    this.setState({ isPageViewsLoading: true })
    try {
      const response = await this.fetchViewData('page_views')
      this.setState({
        pageViewData: response,
        isPageViewsLoading: false
      })
    } catch {
      this.setState({ isPageViewsLoading: false })
    }
  }

  async fetchActiveCustomersData () {
    this.setState({ isTotalVsActiveCustomersLoading: true })
    const { startDate, endDate } = this.state
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false)
    }
    try {
      const { data } = await axios.post('/analyticsV2/fetchActiveCustomersData',
      {
        filters: filterOptions
      })
      this.setState({
        activeCustomersData: data,
        isTotalVsActiveCustomersLoading: false
      })
    } catch {
      this.setState({ isTotalVsActiveCustomersLoading: false })
    }
  }

  async fetchTotalCustomers () {
    this.setState({ isPercentageMonthlyActiveCustomersLoading : true })
    try {
      const {startDate, endDate} = this.state
      const filterOptions = {
        start: AnalyticsDateFormatter.formatDate(startDate),
        end: AnalyticsDateFormatter.formatDate(endDate, false)
      }
      const res = await axios.get(`/company/customers?start=${filterOptions.start}&end=${filterOptions.end}`)
      this.setState({
        customers: res.data,
        isPercentageMonthlyActiveCustomersLoading : false
      })
    } catch {
      this.setState({ isPercentageMonthlyActiveCustomersLoading : false })
    }
  }

  async fetchViewData (viewName: string) {
    const { startDate, endDate } = this.state
    const filterOptions = {
      start: AnalyticsDateFormatter.formatDate(startDate),
      end: AnalyticsDateFormatter.formatDate(endDate, false)
    }
    const res = await axios.post('/analyticsV2/fetchViewData',
      {
        view: viewName,
        filters: filterOptions
      })
    return res.data
  }

  async refetchData () {
    this.setState({ isLoading: true })

    try {
      this.fetchPageViewData()
      this.fetchUniqueVisitorsData()
      this.fetchActiveCustomersData()
      this.fetchTotalCustomers()
      this.setState({ isLoading: false })

    } catch {
      this.setState({ isLoading: false })
    }
  }

  toggleDateRange (value: boolean) {
    this.setState({ isDateRangeOpen: value }, this.addHandler)
  }

  onPresetDateRangeChanged (selectedDate: FilterOptionDate) {
    this.setState({
      selectedPresetDateRangeId: selectedDate.id,
      selectedDate: selectedDate
    })
  }

  onCustomDateRangeChanged (dates: Date[]) {
    const customSelectedDate = {
      id: 'custom_range',
      label: 'Custom Range',
      startDate: dates[0],
      endDate: dates[1]
    }
    this.setState({
      selectedDate: customSelectedDate,
      selectedPresetDateRangeId: customSelectedDate.id
    })
  }

  onDateRangeChanged () {
    const { selectedDate, selectedPresetDateRangeId } = this.state
    let updatedState = Object.assign({}, this.state.storedState)
    updatedState.presetDateRangeId = selectedPresetDateRangeId
    updatedState.selectedDateRange = selectedDate
    this.setState({
      isDateRangeOpen: false,
      startDate: new Date(selectedDate.startDate),
      endDate: new Date(selectedDate.endDate),
      storedState: updatedState
    })
    const queryString = new QueryParameters()
    queryString.set('date_range', selectedPresetDateRangeId)
    if (selectedPresetDateRangeId != 'custom_range') {
      queryString.unset('date_from')
        .unset('date_till')
    } else {
      queryString.set('date_from', AnalyticsDateFormatter.formatDateForUrl(selectedDate.startDate))
        .set('date_till', AnalyticsDateFormatter.formatDateForUrl(selectedDate.endDate))
    }
    window.history.pushState(null, 'date range changed', queryString.getQueryString())
    this.refetchData()
  }

    onResetDateRange () {
      const { presetDateRangeId, selectedDateRange } = this.state.storedState
      this.setState({
        isDateRangeOpen: false,
        selectedPresetDateRangeId: presetDateRangeId,
        selectedDate: selectedDateRange
      })
    }

    getBarChartOptions (displayLegend: boolean = true, minUnit: TimeUnit = 'day'): ChartOptions {
      return {
        title: {
          display: false
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 0,
            bottom: 24
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          intersect: false,
          mode: 'x',
          callbacks: {
            title (item: ChartTooltipItem[], data: ChartData): string | string[] {
              if (item.length > 0 && item[0].label) {
                const date = new Date(item[0].label.substr(0, 10))
                if (minUnit === 'month') {
                  return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(date)
                }
                const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date)
                const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date)
                return `${month} ${day}`
              }
              return ''
            }
          }
        },
        legend: {
          display: displayLegend,
          position: 'top',
          align: 'start',
          labels: {
            boxWidth: 12
          }
        },
        scales: {
          xAxes: [{
            offset: true,
            type: 'time',
            time: {
              minUnit: minUnit
            },
            gridLines: {
              display: false
            },
            ticks: {
              maxRotation: 0,
              callback: (value, index, values) => {
                return '      ' + value + '      '
              }
            }
          }],
          yAxes: [
            {
              gridLines: {
                borderDash: [8, 4],
                color: this.borderGrey,
                drawBorder: false
              },
              ticks: {
                padding: 16,
                beginAtZero: true
              }
            }
          ]
        }
      }
    }

    getBarChartDataDataSet (data: Array<Data>, label: string) {
      return {
        labels: data.map(d => d.time),
        datasets: [
          {
            label: label,
            fill: false,
            data: data.map(d => d.count),
            borderColor: this.brandBlue,
            backgroundColor: this.brandBlue
          }
        ]
      }
    }

    render (_: Props, { pageViewData, uniqueVisitorsData, activeCustomersData, customers, isLoading, isDateRangeOpen, selectedPresetDateRangeId, startDate, endDate, presetDateRangeList, selectedDate, isPageViewsLoading, isUniqueVisitorsLoading, isTotalVsActiveCustomersLoading, isPercentageMonthlyActiveCustomersLoading, isSidebarMenuOpen, shouldChartRedraw }: State) {
      const uniqueVisitorsLabel = 'Unique visitors'
      const pageViewsLabel = 'Page views'
      const activeCustomersPercentageLabel = '% monthly active customers'
      let activeCustomersPercentageDataSet = {}

      const activeCustomersDataSet = {
        datasets: [
          {
            label: 'Total active customers',
            data: activeCustomersData.map(d => { return { x: d.time, y: d.count }}),
            borderColor: this.brandBlue,
            backgroundColor: this.brandBlue
          },
          {
            label: 'Total customers',
            data: customers.map(d => { return { x: d.time, y: d.count }}),
            borderColor: this.brandYellow,
            backgroundColor: this.brandYellow
          }
        ]
      }

      if (Array.isArray(customers) && customers.length) {
        const customersTranspose = customers.reduce((result: Hash<number>, {time, count}) => { result[time] = count; return result }, {})
        activeCustomersPercentageDataSet = {
          labels: activeCustomersData.map(d => d.time),
          datasets: [
            {
              label: activeCustomersPercentageLabel,
              fill: false,
              data: activeCustomersData.map((value, index) => {
                let mac = (value.count / customersTranspose[value.time] || 0) * 100
                return mac.toFixed()
              }),
              borderColor: this.brandBlue,
              backgroundColor: this.brandBlue
            }
          ]
        }
      }

      return (
        <div className={`engagement-dashboard-overview dashboard-overview  ${isSidebarMenuOpen ? 'dashboard-overview-open-menu' : 'dashboard-overview-closed-menu'}`} style={{ width: `calc(100vw - ${isSidebarMenuOpen ? '376' : '152'}px)` }}>
          <Header
            title='Engagement Dashboard'
            disableCancelButton
            disableSaveButton
            isBeta
          />
           <div className='filter-container' ref={node => this.wrapperRef = node}>
             <DashboardDateRangeFilter isLoading={isLoading}
              startDate={startDate}
              endDate={endDate}
              isDropdownOpen={isDateRangeOpen}
              items={presetDateRangeList}
              selectedDate={selectedDate}
              selectedPresetId={selectedPresetDateRangeId}
              toggleDropdown={this.toggleDateRange}
              onChange={this.onDateRangeChanged}
              onReset={this.onResetDateRange}
              onCustomDateChange={this.onCustomDateRangeChanged}
              onPresetDateChange={this.onPresetDateRangeChanged} />
          </div>
          <div className='graph-wrapper'>
            <div className='graph-container' ref={element => { this.barChart = element }}>
               <div className='graph-row'>
               <GraphTitle title='Page views'
                           tooltipMessage='The total number of community pages loaded or reloaded in the selected time period.' />
                 {isPageViewsLoading ? <Loader />
                 : <Bar redraw={shouldChartRedraw} data={this.getBarChartDataDataSet(pageViewData, pageViewsLabel)} options={this.getBarChartOptions(false)} />}
              </div>
              <div className='graph-row'>
                <GraphTitle title='Unique visitors'
                            tooltipMessage='The total number of unique visitors within the selected time period, identified by tracking cookie.' />
                {isUniqueVisitorsLoading ? <Loader />
                : <Bar redraw={shouldChartRedraw} data={this.getBarChartDataDataSet(uniqueVisitorsData, uniqueVisitorsLabel)} options={this.getBarChartOptions(false)} />}
              </div>
              {Array.isArray(customers) && customers.length ? <div className='graph-row'>
                <GraphTitle title='Total active customers / total customers'
                            tooltipMessage="The total number of companies where one or more users linked to the company had at least one page view in a given calendar month. / The total number of companies where the company status was marked as 'customer' in a given calendar month." />
                    {isTotalVsActiveCustomersLoading ? <Loader /> : <Bar redraw={shouldChartRedraw} data={activeCustomersDataSet} options={this.getBarChartOptions(true, 'month')} />}
              </div> : null}

              {Array.isArray(customers) && customers.length ? <div className='graph-row'>
                <GraphTitle title='% monthly active customers'
                            tooltipMessage='The percentage of active customers out of the total number of customers in a given calendar month.' />
                    {isPercentageMonthlyActiveCustomersLoading ? <Loader /> : <Bar redraw={shouldChartRedraw} data={activeCustomersPercentageDataSet} options={this.getBarChartOptions(false, 'month')} />}
              </div> : null}
            </div>
          </div>
          <DashboardTimezoneDisclaimer />
        </div>)
    }
}
export default EngagementGraphs
